/* =========================================================================
 *  fm-megamenu-thumbs.js — hydrate deferred megamenu thumbnails
 *  ------------------------------------------------------------
 *  Added 19-Aug-2026 (site-speed work).
 *
 *  WHY THIS EXISTS
 *  The desktop megamenu panel is `display: block; visibility: hidden;
 *  opacity: 0` until its parent <li> is hovered. `visibility: hidden`
 *  still generates a layout box, so every `loading="lazy"` image inside
 *  the panel intersects the viewport and downloads on page load — while
 *  being completely invisible. Measured on the homepage at 1400px:
 *  93 panel images = 844 KB = 48% of all image bytes, on EVERY template
 *  because the header is global.
 *
 *  `snippets/mega-menu-panel.liquid` therefore ships a 1x1 transparent GIF
 *  in `src` and the real URL in `data-mm-src`. This file swaps them in at
 *  the first sign of nav intent.
 *
 *  Only the image PAYLOAD is deferred. Every <a href> and text label is
 *  server-rendered, so crawlers and the internal-linking/SEO work are
 *  unaffected — an explicit guardrail in the performance audit.
 *
 *  NO VIEWPORT GATE, DELIBERATELY. Below 992px `.fm-megamenu` is
 *  `display: none !important`, and a display:none subtree cannot receive
 *  pointer or focus events — so mobile never triggers hydration without us
 *  testing `innerWidth` at all. That matters here: load-time viewport gates
 *  are a known footgun in this theme's tooling (a sleeping preview pane
 *  reports `innerWidth: 0` and silently takes the mobile branch), so the
 *  cheapest correct thing is to not ask.
 * ========================================================================= */
(function () {
  'use strict';

  var SEL_NAV = '.eq-header__nav-list';
  var SEL_IMG = 'img[data-mm-src]';
  /* The mobile drawer's opener. Needed because the top-level nav thumbnails in
   * sections/eq-header.liquid (figure.mobile-menu-images) were deferred on
   * 24-Aug-2026 too, and those are the MOBILE menu's thumbnails — waiting for a
   * pointer to land on the nav list itself would show empty squares for a beat
   * after the drawer opens. Hydrating on the hamburger press instead puts the
   * fetches in flight before the drawer has finished animating open.
   *
   * Why they needed deferring at all: below 991px `figure.mobile-menu-images`
   * is `display: block` while its ancestor is only `visibility: hidden`, so the
   * thumbnails have a 40x40 layout box, `loading="lazy"` fires, and all 7
   * downloaded invisibly on every pageview. Measured at 375px on
   * /products/premium-indoor-logo-mats: 27,054 bytes, every mobile pageview.
   * At >=991px the figure is `display: none`, so desktop never paid this —
   * which is why a desktop-only capture does not show it. */
  var SEL_MOBILE_OPEN = '.js-hamburger-menu';

  /* Swap one image's deferred URL into `src`. Idempotent — the attribute is
   * removed on use, so a second call is a no-op. */
  function hydrate(img) {
    var src = img.getAttribute('data-mm-src');
    if (!src) return;
    img.removeAttribute('data-mm-src');
    /* Panel is open (or about to be) and these are small: fetch now rather
     * than leaving it to the lazy heuristic, which would otherwise wait for
     * another intersection check. */
    img.loading = 'eager';
    img.src = src;
  }

  function hydrateWithin(root) {
    if (!root) return 0;
    var imgs = root.querySelectorAll(SEL_IMG);
    for (var i = 0; i < imgs.length; i++) hydrate(imgs[i]);
    return imgs.length;
  }

  function init() {
    var nav = document.querySelector(SEL_NAV);
    if (!nav || !document.querySelector(SEL_IMG)) return;
    var opener = document.querySelector(SEL_MOBILE_OPEN);

    var done = false;

    function run(e) {
      if (done) return;
      done = true;

      /* Hydrate the panel the user is actually heading for FIRST, so the
       * thumbnails for that panel are in flight before the rest. */
      var li = e && e.target && e.target.closest
        ? e.target.closest('li.mega-menu-hover, li')
        : null;
      if (li) hydrateWithin(li);

      /* Then the remaining panels, once the browser is idle, so opening a
       * second menu item is instant without competing with the first. */
      var rest = function () { hydrateWithin(document); };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(rest, { timeout: 2000 });
      } else {
        setTimeout(rest, 500);
      }

      detach();
    }

    /* `pointerover` on the nav (not on each <li>) fires as soon as the
     * cursor crosses into the nav strip — a little earlier than the <li>
     * itself, which buys the fetch a head start on the CSS reveal. */
    var events = [
      ['pointerover', nav],
      ['focusin', nav],
      ['touchstart', nav],
      ['keydown', document]   /* Tab-ing into the nav without a pointer */
    ];

    /* Mobile: the drawer opener, so its thumbnails are already loading by the
     * time the drawer is on screen. Guarded because the hamburger is not in
     * every header variant. */
    if (opener) {
      events.push(['pointerdown', opener]);
      events.push(['click', opener]);
    }

    function detach() {
      for (var i = 0; i < events.length; i++) {
        events[i][1].removeEventListener(events[i][0], run, true);
      }
    }

    for (var i = 0; i < events.length; i++) {
      events[i][1].addEventListener(events[i][0], run, {
        capture: true,
        passive: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
